import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ModuleGuard } from '@/components/module-guard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/payroll/structures/$structureId')({
  component: StructureDetailPage,
})

function StructureDetailPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <StructureDetail />
    </ModuleGuard>
  )
}

type StructureItem = {
  id: number
  componentId: number
  calculationType: string
  amount: string | number
  sortOrder?: number | null
  component?: { name: string; code: string; type: string } | null
}

type StructureData = {
  id: number
  name: string
  description?: string | null
  currency?: string | null
  isActive?: boolean | null
  items?: StructureItem[]
}

type Employee = {
  id: number
  firstName: string
  lastName: string
}

type AssignmentRow = {
  assignment: {
    id: number
    employeeId: number
    structureId: number
    baseAmount: string | number
    effectiveFrom: string
    effectiveTo?: string | null
  }
  employee?: { firstName: string; lastName: string } | null
}

const addItemSchema = z.object({
  componentId: z.number().min(1, 'Component required'),
  calculationType: z.enum(['fixed', 'percent_of_base', 'percent_of_gross']),
  amount: z.string().min(1, 'Amount required'),
  sortOrder: z.number().optional(),
})

const assignSchema = z.object({
  employeeId: z.number().min(1, 'Employee required'),
  baseAmount: z.string().min(1, 'Base amount required'),
  effectiveFrom: z.string().min(1, 'Effective from required'),
  effectiveTo: z.string().optional(),
})

const editSchema = z.object({
  name: z.string().min(1, 'Name required'),
  description: z.string().optional(),
  currency: z.string().optional(),
})

type CalcType = 'fixed' | 'percent_of_base' | 'percent_of_gross'
type AddItemFormValues = z.infer<typeof addItemSchema>
type AssignFormValues = z.infer<typeof assignSchema>
type EditFormValues = z.infer<typeof editSchema>

function StructureDetail() {
  const params = Route.useParams()
  const structureId = Number(params.structureId)
  const [isAddItemOpen, setIsAddItemOpen] = useState(false)
  const [isAssignOpen, setIsAssignOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)

  const queryClient = useQueryClient()

  const { data: structureData, isLoading } = useQuery(
    orpc.hr.getSalaryStructure.queryOptions({ input: { id: structureId } }),
  )

  const { data: componentsData } = useQuery(
    orpc.hr.listSalaryComponents.queryOptions({ input: {} }),
  )

  const { data: assignmentsData } = useQuery(
    orpc.hr.listSalaryAssignments.queryOptions({ input: { structureId } }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const structure = structureData as StructureData | null | undefined
  const components = (componentsData ?? []) as { id: number; name: string; code: string; type: string }[]
  const assignments = (assignmentsData ?? []) as unknown as AssignmentRow[]
  const employees = ((employeesData as { data?: Employee[] } | null | undefined)?.data ?? []) as Employee[]

  const addItemMutation = useMutation(
    orpc.hr.addStructureItem.mutationOptions({
      onSuccess: () => {
        toast.success('Item added')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getSalaryStructure']] })
        setIsAddItemOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const removeItemMutation = useMutation(
    orpc.hr.removeStructureItem.mutationOptions({
      onSuccess: () => {
        toast.success('Item removed')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getSalaryStructure']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const assignMutation = useMutation(
    orpc.hr.assignSalary.mutationOptions({
      onSuccess: () => {
        toast.success('Employee assigned')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryAssignments']] })
        setIsAssignOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.hr.updateSalaryStructure.mutationOptions({
      onSuccess: () => {
        toast.success('Structure updated')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getSalaryStructure']] })
        setIsEditOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const endAssignmentMutation = useMutation(
    orpc.hr.endSalaryAssignment.mutationOptions({
      onSuccess: () => {
        toast.success('Assignment ended')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryAssignments']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!structure) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Structure not found</p>
      </div>
    )
  }

  const items = (structure.items ?? []) as StructureItem[]

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/hr/payroll/structures">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Structures
          </Link>
        </Button>
      </div>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{structure.name}</h1>
          {structure.description && (
            <p className="text-muted-foreground">{structure.description}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-muted-foreground">{structure.currency ?? 'USD'}</span>
            <Badge
              variant="outline"
              className={
                structure.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }
            >
              {structure.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      {/* Items Section */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Components</CardTitle>
            <Button size="sm" onClick={() => setIsAddItemOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
          <CardDescription>Earnings and deductions in this structure</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No components added yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Calculation</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Sort Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...items]
                  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {item.component?.name ?? `Component #${item.componentId}`}
                      </TableCell>
                      <TableCell>
                        {item.component && (
                          <Badge
                            variant="outline"
                            className={
                              item.component.type === 'earning'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }
                          >
                            {item.component.type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="capitalize">{item.calculationType}</TableCell>
                      <TableCell>
                        {Number(item.amount).toLocaleString()}
                        {item.calculationType !== 'fixed' ? '%' : ''}
                      </TableCell>
                      <TableCell>{item.sortOrder ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => removeItemMutation.mutate({ id: item.id })}
                          disabled={removeItemMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assignments Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Employee Assignments</CardTitle>
            <Button size="sm" onClick={() => setIsAssignOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Assign Employee
            </Button>
          </div>
          <CardDescription>Employees assigned to this salary structure</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {assignments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No employees assigned yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Base Amount</TableHead>
                  <TableHead>Effective From</TableHead>
                  <TableHead>Effective To</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((row) => {
                  const a = row.assignment
                  const empName = row.employee
                    ? `${row.employee.firstName} ${row.employee.lastName}`
                    : `Employee #${a.employeeId}`
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{empName}</TableCell>
                      <TableCell>
                        {Number(a.baseAmount).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        {new Date(a.effectiveFrom).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {a.effectiveTo
                          ? new Date(a.effectiveTo).toLocaleDateString()
                          : 'Ongoing'}
                      </TableCell>
                      <TableCell className="text-right">
                        {!a.effectiveTo && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const today = new Date().toISOString().split('T')[0]
                              endAssignmentMutation.mutate({
                                id: a.id,
                                effectiveTo: today,
                              })
                            }}
                            disabled={endAssignmentMutation.isPending}
                          >
                            End
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Item Dialog */}
      <AddItemDialog
        open={isAddItemOpen}
        onOpenChange={setIsAddItemOpen}
        structureId={structureId}
        components={components}
        onSubmit={async (data) => {
          await addItemMutation.mutateAsync({
            structureId,
            componentId: data.componentId,
            calculationType: data.calculationType,
            amount: Number(data.amount),
            sortOrder: data.sortOrder,
          })
        }}
      />

      {/* Assign Employee Dialog */}
      <AssignEmployeeDialog
        open={isAssignOpen}
        onOpenChange={setIsAssignOpen}
        structureId={structureId}
        employees={employees}
        onSubmit={async (data) => {
          await assignMutation.mutateAsync({
            employeeId: data.employeeId,
            structureId,
            baseAmount: Number(data.baseAmount),
            effectiveFrom: data.effectiveFrom,
            effectiveTo: data.effectiveTo || undefined,
          })
        }}
      />

      {/* Edit Structure Dialog */}
      <EditStructureDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        structure={structure}
        onSubmit={async (data) => {
          await updateMutation.mutateAsync({ id: structureId, ...data })
        }}
      />
    </div>
  )
}

interface AddItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  structureId: number
  components: { id: number; name: string; code: string; type: string }[]
  onSubmit: (data: AddItemFormValues) => Promise<void>
}

function AddItemDialog({ open, onOpenChange, components, onSubmit }: AddItemDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AddItemFormValues>({
    resolver: zodResolver(addItemSchema),
    defaultValues: { calculationType: 'fixed', amount: '' },
  })

  const handleSubmit = async (data: AddItemFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Add Component</DialogTitle>
          <DialogDescription>Add a salary component to this structure</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="componentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Component *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value ? field.value.toString() : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select component" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {components.map((c) => (
                        <SelectItem key={c.id} value={c.id.toString()}>
                          {c.name} ({c.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="calculationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Calculation Type *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v as CalcType)}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="percent_of_base">% of Base</SelectItem>
                      <SelectItem value="percent_of_gross">% of Gross</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="1"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Component'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface AssignEmployeeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  structureId: number
  employees: Employee[]
  onSubmit: (data: AssignFormValues) => Promise<void>
}

function AssignEmployeeDialog({ open, onOpenChange, employees, onSubmit }: AssignEmployeeDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AssignFormValues>({
    resolver: zodResolver(assignSchema),
    defaultValues: { baseAmount: '', effectiveFrom: '' },
  })

  const handleSubmit = async (data: AssignFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Assign Employee</DialogTitle>
          <DialogDescription>Assign an employee to this salary structure</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value ? field.value.toString() : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="baseAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base Amount *</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="0.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="effectiveFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective From *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="effectiveTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective To</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Assigning...' : 'Assign Employee'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface EditStructureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  structure: StructureData
  onSubmit: (data: EditFormValues) => Promise<void>
}

function EditStructureDialog({ open, onOpenChange, structure, onSubmit }: EditStructureDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: structure.name,
      description: structure.description ?? '',
      currency: structure.currency ?? 'USD',
    },
  })

  const handleSubmit = async (data: EditFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Edit Structure</DialogTitle>
          <DialogDescription>Update salary structure details</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl>
                    <Input placeholder="USD" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
